import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Pencil, Trash2, Plus } from "lucide-react";
import { GymArticle, useGymArticles } from "@/hooks/payments/useGymArticles";
import { ArticleForm } from "./ArticleForm";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface ArticleManagerProps {
  gymId: string;
  onBack: () => void;
}

export const ArticleManager = ({ gymId, onBack }: ArticleManagerProps) => {
  const { articles, createArticle, updateArticle, deleteArticle, isLoading } = useGymArticles(gymId);
  const [editing, setEditing] = useState<GymArticle | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleting, setDeleting] = useState<GymArticle | null>(null);

  const formatCurrency = (n: number) => `$${Number(n).toLocaleString()}`;

  const handleCreate = async (data: { name: string; description: string | null; price: number }) => {
    await createArticle.mutateAsync({ gym_id: gymId, ...data });
    setIsCreating(false);
  };

  const handleUpdate = async (data: { name: string; description: string | null; price: number }) => {
    if (!editing) return;
    await updateArticle.mutateAsync({ id: editing.id, data });
    setEditing(null);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await deleteArticle.mutateAsync(deleting.id);
    setDeleting(null);
  };

  if (isCreating) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="icon" onClick={() => setIsCreating(false)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-medium">Nuevo artículo</h3>
        </div>
        <ArticleForm
          onSubmit={handleCreate}
          onCancel={() => setIsCreating(false)}
          isLoading={createArticle.isPending}
        />
      </div>
    );
  }

  if (editing) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="icon" onClick={() => setEditing(null)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h3 className="font-medium">Editar artículo</h3>
        </div>
        <ArticleForm
          article={editing}
          onSubmit={handleUpdate}
          onCancel={() => setEditing(null)}
          isLoading={updateArticle.isPending}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="icon" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h3 className="font-medium">Gestionar artículos</h3>
      </div>

      <p className="text-sm text-muted-foreground">Artículos disponibles:</p>

      {isLoading ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Cargando...</p>
      ) : articles.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          No hay artículos creados
        </p>
      ) : (
        <div className="space-y-2">
          {articles.map((article) => (
            <div key={article.id} className="border rounded-md p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-medium">{article.name}</span>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setEditing(article)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => setDeleting(article)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {formatCurrency(article.price)}
                {article.description ? ` · ${article.description}` : ""}
              </p>
            </div>
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => setIsCreating(true)}
      >
        <Plus className="h-4 w-4 mr-2" />
        Agregar artículo
      </Button>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar artículo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El artículo "{deleting?.name}" será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
